import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const ScrambleText = ({ text, delay = 0 }) => {
  const [scrambledText, setScrambledText] = useState(text);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const repeatTimeoutRef = useRef(null);
  
  useEffect(() => {
    const chars = '!@#$%^&*()_+1234567890abcdefghijklmnopqrstuvwxyz';
    
    const startScramble = () => {
      let iteration = 0;
      const originalText = text;
      const intervalDuration = 100;
      
      const interval = setInterval(() => {
        let newText = '';
        for (let i = 0; i < originalText.length; i++) {
          if (i < iteration) {
            newText += originalText[i];
          } else {
            newText += chars[Math.floor(Math.random() * chars.length)];
          }
        }
        
        setScrambledText(newText);
        
        if (iteration >= originalText.length) {
          clearInterval(interval);
          repeatTimeoutRef.current = setTimeout(() => {
            startScramble();
          }, 5000);
        }
        
        iteration++;
      }, intervalDuration);
      
      intervalRef.current = interval;
    };
    
    timeoutRef.current = setTimeout(() => {
      startScramble();
    }, delay * 1000);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (repeatTimeoutRef.current) clearTimeout(repeatTimeoutRef.current);
    };
  }, [text, delay]);
  
  return (
    <span style={{ 
      display: 'inline-block', 
      minWidth: `${text.length}ch`,
      fontFeatureSettings: '"tnum"',
      willChange: 'contents'
    }}>
      {scrambledText}
    </span>
  );
};

const About = () => {
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full flex items-start justify-center px-4 sm:px-6 lg:px-8 pt-8 pb-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          
          {/* Profile Photo Section */}
          <motion.div 
            className="flex justify-center lg:justify-start lg:pr-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              {/* Animated background circle */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary-green to-primary-cyan rounded-full opacity-20"
                animate={{ 
                  scale: [1, 1.15, 1],
                  rotate: [0, 360]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  width: '110%',
                  height: '110%',
                  top: '-5%',
                  left: '-5%'
                }}
              />
              
              {/* Profile photo with image */}
              <motion.div
                className="relative w-[420px] h-[420px] rounded-full bg-gradient-to-br from-primary-green to-primary-cyan flex items-center justify-center shadow-2xl"
                animate={{ y: [0, -20, 0] }}
                transition={{ 
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="w-[388px] h-[388px] rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <img
                    src="/pictures/image.jpeg"
                    alt="Vivek Yadav"
                    className="w-full h-full object-cover"
                    style={{
                      imageRendering: "crisp-edges",
                      backfaceVisibility: "hidden",
                    }}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentNode.innerHTML = `
                        <div class="text-center">
                          <div class="text-6xl mb-4">👨‍💻</div>
                          <div class="text-white font-semibold">Vivek Kumar Yadav</div>
                          <div class="text-sm text-gray-300">ML & Web Developer</div>
                        </div>
                      `;
                    }}
                  />
                </div>
              </motion.div>
              
              {/* Floating elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-8 h-8 bg-primary-cyan rounded-full"
                animate={{ 
                  y: [0, -10, 0],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-6 h-6 bg-primary-green rounded-full"
                animate={{ 
                  y: [0, 10, 0],
                  scale: [1, 1.3, 1]
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Hi, I'm{' '}
              <span className="gradient-text">Vivek</span>
            </motion.h1>
            
            <motion.h2 
              className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-6 sm:mb-8 whitespace-normal w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{ color: '#00aeb5' }}
            >
              <div className="flex flex-col items-start">
                <ScrambleText text="Machine Learning and" delay={0.6} />
                <ScrambleText text="Web Development Enthusiast" delay={0.7} />
              </div>
            </motion.h2>
            
            <motion.p 
              className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8 max-w-3xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Passionate ML and web development enthusiast blending data-driven insights with full-stack expertise. 
              I'm Vivek Yadav, a Mac-based coder adept at building intelligent systems and dynamic web apps.
            </motion.p>
            
            <motion.p 
              className="text-base sm:text-lg text-gray-700 leading-relaxed mb-8 sm:mb-12 max-w-3xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              Skilled in Python, C++, PHP, JavaScript, and frameworks like Node.js and React, I leverage XAMPP, 
              MySQL, and AI-powered editors like Cursor AI to accelerate development and ensure code quality. 
              I believe in continuous learning, real-world impact, and crafting seamless user experiences. 
              Let's innovate together.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
            >
              <motion.button
                onClick={() => scrollToSection('portfolio')}
                className="px-6 py-3 sm:px-8 sm:py-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ backgroundColor: 'hsl(109, 97%, 28%)' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View My Work
              </motion.button>
              
              <motion.button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-cyan-400 text-cyan-400 font-semibold rounded-lg hover:bg-cyan-400 hover:text-gray-900 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get In Touch
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;













// import React, { useEffect, useRef, useState } from 'react';
// import { motion } from 'framer-motion';

// const ScrambleText = ({ text, delay = 0 }) => {
//   const [scrambledText, setScrambledText] = useState(text);
//   const intervalRef = useRef(null);
//   const timeoutRef = useRef(null);
//   const repeatTimeoutRef = useRef(null);
  
//   useEffect(() => {
//     const chars = '!@#$%^&*()_+1234567890abcdefghijklmnopqrstuvwxyz';
    
//     const startScramble = () => {
//       let iteration = 0;
//       const originalText = text;
//       // Slowing down the animation by increasing interval duration from 50ms to 100ms
//       const intervalDuration = 100;
      
//       const interval = setInterval(() => {
//         let newText = '';
//         for (let i = 0; i < originalText.length; i++) {
//           if (i < iteration) {
//             newText += originalText[i];
//           } else {
//             newText += chars[Math.floor(Math.random() * chars.length)];
//           }
//         }
        
//         setScrambledText(newText);
        
//         if (iteration >= originalText.length) {
//           clearInterval(interval);
          
//           // Wait 5 seconds before starting the next scramble
//           repeatTimeoutRef.current = setTimeout(() => {
//             startScramble();
//           }, 5000);
//         }
        
//         iteration++;
//       }, intervalDuration);
      
//       intervalRef.current = interval;
//     };
    
//     // Start the initial scramble after the specified delay
//     timeoutRef.current = setTimeout(() => {
//       startScramble();
//     }, delay * 1000);
    
//     // Clean up intervals and timeouts
//     return () => {
//       if (timeoutRef.current) clearTimeout(timeoutRef.current);
//       if (intervalRef.current) clearInterval(intervalRef.current);
//       if (repeatTimeoutRef.current) clearTimeout(repeatTimeoutRef.current);
//     };
//   }, [text, delay]);
  
//   return (
//     // Added CSS to prevent layout shifts during scrambling
//     <span style={{ 
//       display: 'inline-block', 
//       minWidth: `${text.length}ch`,
//       fontFeatureSettings: '"tnum"',
//       willChange: 'contents'
//     }}>
//       {scrambledText}
//     </span>
//   );
// };

// const About = () => {
//   // Simple navigation function for internal links
//   const scrollToSection = (sectionId) => {
//     const section = document.getElementById(sectionId);
//     if (section) {
//       window.scrollTo({
//         top: section.offsetTop,
//         behavior: 'smooth'
//       });
//     }
//   };

//   return (
//     <div className="w-full flex items-start justify-center px-4 sm:px-6 lg:px-8 pt-8 pb-12">

//       <div className="max-w-6xl mx-auto">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          
//           {/* Profile Photo Section */}
//           <motion.div 
//             className="flex justify-center lg:justify-start lg:pr-8"
//             initial={{ opacity: 0, x: -20 }}
//             animate={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8 }}
//           >
//             <div className="relative">
//               {/* Animated background circle */}
//               <motion.div
//                 className="absolute inset-0 bg-gradient-to-r from-primary-green to-primary-cyan rounded-full opacity-20"
//                 animate={{ 
//                   scale: [1, 1.15, 1],
//                   rotate: [0, 360]
//                 }}
//                 transition={{ 
//                   duration: 8,
//                   repeat: Infinity,
//                   ease: "linear"
//                 }}
//                 style={{
//                   width: '110%',
//                   height: '110%',
//                   top: '-5%',
//                   left: '-5%'
//                 }}
//               />
              
//               {/* Profile photo with image */}
//               <motion.div
//                 className="relative w-[420px] h-[420px] rounded-full bg-gradient-to-br from-primary-green to-primary-cyan flex items-center justify-center shadow-2xl"
//                 animate={{ y: [0, -20, 0] }}
//                 transition={{ 
//                   duration: 6,
//                   repeat: Infinity,
//                   ease: "easeInOut"
//                 }}
//               >
//                 <div className="w-[388px] h-[388px] rounded-full bg-white flex items-center justify-center overflow-hidden">
//                   <img
//                     src="/pictures/about.JPG"
//                     alt="Vivek Yadav"
//                     className="w-full h-full object-cover"
//                     style={{
//                       imageRendering: "crisp-edges",
//                       backfaceVisibility: "hidden",
//                     }}
//                     loading="eager"
//                     decoding="async"
//                     fetchPriority="high"
//                     onError={(e) => {
//                       e.target.style.display = "none";
//                       e.target.parentNode.innerHTML = `
//                         <div class="text-center">
//                           <div class="text-6xl mb-4">👨‍💻</div>
//                           <div class="text-white font-semibold">Vivek Kumar Yadav</div>
//                           <div class="text-sm text-gray-300">ML & Web Developer</div>
//                         </div>
//                       `;
//                     }}
//                   />
//                 </div>
//               </motion.div>
              
//               {/* Floating elements */}
//               <motion.div
//                 className="absolute -top-4 -right-4 w-8 h-8 bg-primary-cyan rounded-full"
//                 animate={{ 
//                   y: [0, -10, 0],
//                   scale: [1, 1.2, 1]
//                 }}
//                 transition={{ 
//                   duration: 3,
//                   repeat: Infinity,
//                   ease: "easeInOut"
//                 }}
//               />
//               <motion.div
//                 className="absolute -bottom-4 -left-4 w-6 h-6 bg-primary-green rounded-full"
//                 animate={{ 
//                   y: [0, 10, 0],
//                   scale: [1, 1.3, 1]
//                 }}
//                 transition={{ 
//                   duration: 4,
//                   repeat: Infinity,
//                   ease: "easeInOut",
//                   delay: 1
//                 }}
//               />
//             </div>
//           </motion.div>

//           {/* Content Section */}
//           <motion.div
//             initial={{ opacity: 0, x: 20 }}
//             animate={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8, delay: 0.2 }}
//           >
//             <motion.h1 
//               className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 0.4 }}
//             >
//               Hi, I'm{' '}
//               <span className="gradient-text">Vivek</span>
//             </motion.h1>
            
//             <motion.h2 
//               className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-6 sm:mb-8 whitespace-normal w-full"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 0.6 }}
//               style={{ color: '#00aeb5' }}
//             >
//               <div className="flex flex-col items-start">
//                 <ScrambleText text="Machine Learning and" delay={0.6} />
//                 <ScrambleText text="Web Development Enthusiast" delay={0.7} />
//               </div>
//             </motion.h2>
            
//             <motion.p 
//               className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8 max-w-3xl"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 0.8 }}
//             >
//               Passionate ML and web development enthusiast blending data-driven insights with full-stack expertise. 
//               I'm Vivek Yadav, a Mac-based coder adept at building intelligent systems and dynamic web apps.
//             </motion.p>
            
//             {/* <motion.p 
//               className="text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 1.0 }}
//             >
//               From developing CPU scheduler simulators and smart irrigation solutions to training scikit‑learn 
//               models for house price prediction and crop growth analysis, I thrive on turning complex algorithms 
//               into practical tools.
//             </motion.p>
//              */}
//             <motion.p 
//               className="text-base sm:text-lg text-gray-300 leading-relaxed mb-8 sm:mb-12 max-w-3xl"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 1.2 }}
//             >
//               Skilled in Python, C++, PHP, JavaScript, and frameworks like Node.js and React, I leverage XAMPP, 
//               MySQL, and AI-powered editors like Cursor AI to accelerate development and ensure code quality. 
//               I believe in continuous learning, real-world impact, and crafting seamless user experiences. 
//               Let's innovate together.
//             </motion.p>

//             {/* CTA Buttons */}
//             <motion.div 
//               className="flex flex-col sm:flex-row gap-4"
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.8, delay: 1.4 }}
//             >
//               <motion.button
//                 onClick={() => scrollToSection('portfolio')}
//                 className="px-6 py-3 sm:px-8 sm:py-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
//                 style={{ backgroundColor: 'hsl(109, 97%, 28%)' }}
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//               >
//                 View My Work
//               </motion.button>
              
//               <motion.button
//                 onClick={() => scrollToSection('contact')}
//                 className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-cyan-400 text-cyan-400 font-semibold rounded-lg hover:bg-cyan-400 hover:text-gray-900 transition-all duration-300"
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//               >
//                 Get In Touch
//               </motion.button>
//             </motion.div>
//           </motion.div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default About;